import { useState, useRef, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Map, Marker, useMap } from '@vis.gl/react-google-maps';

import TranslationText from '@Components/Utilities/TranslationText';

const LocationMap = forwardRef((props, ref) => {
    const [isOpen, setIsOpen] = useState(props.isOpen);
    const [isToggling, setIsToggling] = useState(false);
    const [flightPaths, setFlightPaths] = useState([]);
    const cameraChangeTimeout = useRef(null);
    const mapRef = useRef(null);
    const map = useMap();
    const mapBoundsLoaded = useRef(false);

    const handleCameraChange = useCallback((ev) => {
        if (cameraChangeTimeout.current) {
            clearTimeout(cameraChangeTimeout.current);
        }

        if (mapBoundsLoaded.current) {
            cameraChangeTimeout.current = setTimeout(() => {
                props.onLocationChange(ev.detail.bounds);
            }, 1000);
        }
        mapBoundsLoaded.current = true;
    }, [props.onLocationChange]);

    const getIconClass = () => {
        if (isOpen) {
            return 'fas fa-minus';
        } else {
            return 'fas fa-chevron-right';
        }
    };

    const toggleOpen = useCallback((e) => {
        e.preventDefault();
        // Prevent odd toggling behavior switching from mobile to desktop resolution  
        if (!isToggling) {
            setIsToggling(true);
            setIsOpen((prev) => !prev);
            setTimeout(() => setIsToggling(false), 300);
        }
    }, [isToggling]);

    const setMapCenter = (lat, lng, distance) => {
        if (map) {
            map.setCenter({ lat, lng });

            if (distance === 0) {
                map.setZoom(3);
            } else if (distance < 25) {
                map.setZoom(11);
            } else if (distance < 51) {
                map.setZoom(9);
            } else if (distance < 151) {
                map.setZoom(7);
            } else {
                map.setZoom(6);
            }
        }
    };

    useImperativeHandle(ref, () => ({
        setMapCenter
    }));

    useEffect(() => {
        if (props.flightPaths && map) {
            // Remove existing flight paths from the map
            flightPaths.forEach(flightPath => flightPath.setMap(null));

            var arrowSymbol = {
                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                strokeColor: '#FF0000',
            };

            const newFlightPaths = props.flightPaths.map(path => {
                var line = [
                    { lat: path.From.Latitude, lng: path.From.Longitude },
                    { lat: path.To.Latitude, lng: path.To.Longitude }
                ];

                const flightPath = new google.maps.Polyline({
                    path: line,
                    icons: [{
                        icon: arrowSymbol,
                        scale: 5,
                        strokeColor: "#FF0000",
                        offset: '50%'
                    }],
                    geodesic: true,
                    strokeColor: "#FF0000",
                    strokeOpacity: 1.0,
                    strokeWeight: 2
                });
                flightPath.setMap(map);
                return flightPath;
            });

            // Update state with new flight paths
            setFlightPaths(newFlightPaths);
        }
    }, [props.flightPaths, map]);

    return (
        <details open={isOpen} onToggle={toggleOpen}>
            <summary className='geographic-search-header'>
                <TranslationText tKey='sandhills.tradepubwebsite.mvc.geographicsearch' />
                <i className={getIconClass()}></i>
            </summary>
            <div className='map-container'>
                <Map
                    ref={mapRef}
                    defaultCenter={props.defaultCenter ? { lat: props.defaultCenter.Latitude, lng: props.defaultCenter.Longitude } : { lat: 40.8058, lng: -96.6718 }}
                    defaultZoom={props.defaultZoom ? props.defaultZoom : 2}
                    onCameraChanged={handleCameraChange}
                >
                    {props.locations?.map((location, i) => (
                        <Marker
                            key={i}
                            position={{ lat: location.Latitude, lng: location.Longitude }}
                        />
                    ))}
                </Map>
            </div>
        </details>
    );
});

export default LocationMap;
